import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useTimePeriodContext } from "../contexts/TimePeriodContext";
import { ProjectCheck } from "../types/ProjectCheck";
import { SubordinateSummary } from "../types/SubordinateSummary";
import { GetProjectChecksAsync } from "../services/ProjectsService";
import {
  GetSubordinatesStatusesAsync,
  SaveSupervisorProjectCheckChangesAsync,
} from "../services/SupervisorService";

export function useSupervisor() {
  const { instance, account, user, authLoading } = useAuth();
  const { selectedPeriod } = useTimePeriodContext();

  const [selectedSubordinateId, setSelectedSubordinateId] = useState<number>(0);
  const [subordinateStatuses, setSubordinateStatuses] = useState<SubordinateSummary[]>([]);
  const [projectChecks, setProjectChecks] = useState<ProjectCheck[]>([]);
  const [originalProjectChecks, setOriginalProjectChecks] = useState<ProjectCheck[]>([]);

  const [subordinateLoading, setSubordinateLoading] = useState(true);
  const [projectChecksLoading, setProjectChecksLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [saveError, setSaveError] = useState(false);

  const fetchSubordinates = useCallback(async () => {
    if (authLoading || !user?.id) return;
    setSubordinateLoading(true);
    setFetchError(false);
    try {
      const data = await GetSubordinatesStatusesAsync(instance, account, user.id, selectedPeriod);
      if (!data) throw new Error();
      setSubordinateStatuses(data);
    } catch {
      setFetchError(true);
    } finally {
      setSubordinateLoading(false);
    }
  }, [authLoading, user?.id, instance, account, selectedPeriod]);

  useEffect(() => {
    fetchSubordinates();
  }, [fetchSubordinates]);

  const fetchProjectChecks = useCallback(async () => {
    if (!selectedSubordinateId) return;
    setProjectChecksLoading(true);
    setFetchError(false);
    try {
      const data = await GetProjectChecksAsync(
        instance,
        account,
        selectedSubordinateId,
        selectedPeriod,
      );
      if (!data) throw new Error();
      setProjectChecks(data.checks);
      setOriginalProjectChecks(data.checks);
    } catch {
      setFetchError(true);
    } finally {
      setProjectChecksLoading(false);
    }
  }, [selectedSubordinateId, instance, account, selectedPeriod]);

  useEffect(() => {
    fetchProjectChecks();
  }, [fetchProjectChecks]);

  const hasChanges = useMemo(() => {
    return projectChecks.some((current, i) => {
      const original = originalProjectChecks[i];
      return (
        current.checkedBySupervisor !== original?.checkedBySupervisor ||
        current.supervisorComment !== original?.supervisorComment
      );
    });
  }, [projectChecks, originalProjectChecks]);

  const saveProjectChecks = useCallback(async () => {
    setSubordinateLoading(true);
    setSaveError(false);
    try {
      await SaveSupervisorProjectCheckChangesAsync(instance, account, projectChecks);
      setOriginalProjectChecks([...projectChecks]);
      await fetchSubordinates();
    } catch {
      setSaveError(true);
    } finally {
      setSubordinateLoading(false);
    }
  }, [instance, account, projectChecks, fetchSubordinates]);

  const updateCheck = useCallback((id: number, value: boolean | string, type?: "checked") => {
    setProjectChecks((prev) =>
      prev.map((p) =>
        p.id === id
          ? {
              ...p,
              ...(type === "checked"
                ? { checkedBySupervisor: value as boolean }
                : { supervisorComment: value as string }),
            }
          : p,
      ),
    );
  }, []);

  const confirmAll = useCallback(() => {
    setProjectChecks((prev) => prev.map((c) => ({ ...c, checkedBySupervisor: true })));
  }, []);

  return {
    selectedSubordinateId,
    setSelectedSubordinateId,
    subordinateStatuses,
    projectChecks,
    hasChanges,
    subordinateLoading,
    projectChecksLoading,
    fetchError,
    saveError,
    fetchSubordinates,
    saveProjectChecks,
    updateCheck,
    confirmAll,
  };
}
